/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable max-statements */
import { ComposerTranslation } from 'vue-i18n';
import { getLocationContext } from '../GooglePlaces/googlePlacesClient';
import {
  generateNeighbourhoodInsights,
  generateNeighbourhoodInsightsWithPOI,
} from './neighbourhoodAIClient';
import { DEFAULT_PERSONA } from './neighbourhoodConstants';
import {
  AggregatedNeighbourhoodData,
  LocationScore,
  NeighbourhoodInsights,
  ScoreType,
  TagType,
} from './neighbourhoodDescriptionTypes';
import type { NeighbourhoodPoiDescriptionResponse } from './neighbourhoodPOITypes';
import {
  getNeighbourhoodDataSource,
  getPOIDataFromStore,
  shouldUsePOIDataSource,
  type POICacheStore,
} from './poiDataIntegration';

/**
 * Format Google Places data for AI context
 */
function formatGooglePlacesForAI(googlePlacesContext: any) {
  if (!googlePlacesContext) return undefined;

  const formatPlace = (p: any, defaultName: string, includeType = false) => {
    const name = p.displayName?.text || defaultName;
    const rating = p.rating || 'N/A';
    const summary = p.generativeSummary?.overview?.text;
    const type = p.primaryType;

    let result = `${name} (${rating}★)`;
    if (summary) {
      result += ` - ${summary}`;
    }
    if (includeType && type) {
      result = `${name} (${type})`;
    }
    return result;
  };

  return {
    nearbyRestaurants: googlePlacesContext.restaurants
      .slice(0, 5)
      .map((p: any) => formatPlace(p, 'Restaurant')),
    nearbySchools: googlePlacesContext.schools
      .slice(0, 3)
      .map((p: any) => formatPlace(p, 'School')),
    nearbyParks: googlePlacesContext.parks.slice(0, 3).map((p: any) => formatPlace(p, 'Park')),
    nearbyTransit: googlePlacesContext.transit
      .slice(0, 3)
      .map((p: any) => formatPlace(p, 'Transit', true)),
    nearbyShopping: googlePlacesContext.shopping
      .slice(0, 3)
      .map((p: any) => formatPlace(p, 'Shopping')),
  };
}

/**
 * Format Google Places data for map component
 */
function formatGooglePlacesForMap(googlePlacesContext: any) {
  if (!googlePlacesContext) return undefined;

  return {
    restaurants: googlePlacesContext.restaurants,
    schools: googlePlacesContext.schools,
    parks: googlePlacesContext.parks,
    transit: googlePlacesContext.transit,
    shopping: googlePlacesContext.shopping,
  };
}

/**
 * Aggregate neighbourhood data from multiple sources:
 * 1. Google Places API (real amenity data) OR POI Service (reused data)
 * 2. OpenAI Lambda (comprehensive insights)
 * 3. Fallback to mock data if APIs fail
 */

export async function aggregateNeighbourhoodData(
  params: {
    latitude: number;
    longitude: number;
    municipalityId?: number;
    municipalityName?: string;
    propertyType?: string;
    zipCode?: string;
    persona?: string;
    locale?: string;
    enableWebSearch?: boolean;
    listingId: string;
    store?: any; // KK-2200: Kept for API compatibility but no longer used for POI data
    poiCacheStore?: POICacheStore;
    t: ComposerTranslation;
  },
  signal?: AbortSignal,
): Promise<NeighbourhoodPoiDescriptionResponse | AggregatedNeighbourhoodData | null> {
  const {
    latitude,
    longitude,
    // municipalityId,
    municipalityName,
    propertyType,
    zipCode,
    persona,
    locale,
    listingId,
    enableWebSearch,
    t,
  } = params;

  // Create a more specific neighborhood identifier by combining zip code + municipality
  const specificNeighborhood = zipCode
    ? `${zipCode} ${municipalityName || 'This Area'}`
    : municipalityName || 'This Area';

  const region = extractRegionFromMunicipality(municipalityName);

  const usePOI = shouldUsePOIDataSource();
  const dataSource = getNeighbourhoodDataSource();

  try {
    let aiInsights: NeighbourhoodInsights | null = null;
    let googlePlacesData: any = undefined;

    // ============================================================================
    // DATA SOURCE SELECTION: POI Service vs Google Places
    // ============================================================================

    if (usePOI && listingId) {
      // ========== POI SERVICE PATH (Zero cost, reuses Travel Time data) ==========

      // Step 1: Get POI data from store (or fetch on-demand if not available)
      const poiContext = await getPOIDataFromStore({
        listingId,
        latitude,
        longitude,
        persona,
        poiCacheStore: params.poiCacheStore,
        t,
      });
      // Step 2: Generate AI insights via AWS Lambda with POI context
      const poiResponse = await generateNeighbourhoodInsightsWithPOI(
        {
          latitude,
          longitude,
          municipalityName: specificNeighborhood,
          countryCode: 'CH',
          propertyType,
          zipCode,
          region,
          persona: persona || DEFAULT_PERSONA,
          locale,
          enableWebSearch,
          poiContext,
          listingId,
        },
        signal,
      );

      return poiResponse as any;
    } else {
      // ========== GOOGLE PLACES PATH (Existing, costs ~$0.16/page) ==========

      const googlePlacesContext = await getLocationContext({
        latitude,
        longitude,
        radius: 1000, // 1km radius
      });

      const placesContextForAI = formatGooglePlacesForAI(googlePlacesContext);

      aiInsights = await generateNeighbourhoodInsights(
        {
          latitude,
          longitude,
          municipalityName: specificNeighborhood,
          countryCode: 'CH',
          propertyType,
          zipCode,
          region,
          persona: persona || DEFAULT_PERSONA,
          locale, // Pass locale for localized responses
          listingId,
          googlePlacesContext: placesContextForAI,
        },
        signal,
      );

      googlePlacesData = formatGooglePlacesForMap(googlePlacesContext);
    }

    // ============================================================================
    // PROCESS AI INSIGHTS (same for both data sources)
    // ============================================================================

    if (aiInsights) {
      const filteredAIInsights = {
        ...aiInsights,
        idealFor: aiInsights.idealFor
          ? aiInsights.idealFor.filter((match) => match.persona === DEFAULT_PERSONA)
          : [],
      };

      const result: AggregatedNeighbourhoodData = {
        name: specificNeighborhood, // Use the more specific identifier
        description: aiInsights.description,
        tags: aiInsights.lifestyleTags.map((tag, index) => ({
          id: `ai-tag-${index}`,
          label: tag,
          type: inferTagType(tag),
        })),
        scores: generateMockScoresFromAI(aiInsights),
        aiInsights: filteredAIInsights,

        macroData: {
          priceStatistics: aiInsights.marketData
            ? {
                currentPricePerSqm: aiInsights.marketData.estimatedPricePerSqm,
                priceChange5Year: aiInsights.marketData.priceChange5Year,
                marketTemperature: aiInsights.marketData.marketTemperature,
              }
            : undefined,
          taxData: aiInsights.taxData
            ? {
                municipalityName: municipalityName || 'This Area',
                canton: aiInsights.taxData.canton,
                totalTaxRate: aiInsights.taxData.estimatedTotalTaxRate,
                taxFriendly: aiInsights.taxData.taxFriendly,
                comparisonText: aiInsights.taxData.comparisonToAverage,
              }
            : undefined,
          marketData: aiInsights.marketData
            ? {
                municipalityId: 0, // Not provided by backend
                municipalityName: municipalityName || 'This Area',
                supplyDemandRatio: aiInsights.marketData.marketTemperature === 'hot' ? 85 : 65, // Infer from temperature
                averageDaysOnMarket: aiInsights.marketData.marketTemperature === 'hot' ? 15 : 30, // Infer from temperature
                priceStability: 75, // Default value
                rentalYield: aiInsights.marketData.rentalYield,
                occupancyRate: 100 - (aiInsights.marketData.vacancyRate || 0),
                investmentScore: aiInsights.marketData.investmentScore,
                marketInsights: [
                  `Price: CHF ${aiInsights.marketData.estimatedPricePerSqm}/m²`,
                  `5-year change: ${aiInsights.marketData.priceChange5Year > 0 ? '+' : ''}${aiInsights.marketData.priceChange5Year}%`,
                  `Market: ${aiInsights.marketData.marketTemperature}`,
                  `Rental yield: ${aiInsights.marketData.rentalYield}%`,
                ],
              }
            : undefined,
        },
        googlePlacesData,
        dataSource: 'ai-only' as const,
        lastUpdated: new Date().toISOString(),
      };

      // eslint-disable-next-line max-depth
      if (typeof window !== 'undefined') {
        (window as any).__lastNeighbourhoodData = result;
      }

      return result;
    }

    return generateCompleteMockData(specificNeighborhood, googlePlacesData);
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw error;
    }

    return generateCompleteMockData(specificNeighborhood);
  }
}

/**
 * Infer tag type from AI-generated tag label
 */
function inferTagType(tag: string): TagType {
  const lowerTag = tag.toLowerCase();

  if (lowerTag.includes('family') || lowerTag.includes('child') || lowerTag.includes('kid')) {
    return TagType.Family;
  }
  if (
    lowerTag.includes('transit') ||
    lowerTag.includes('transport') ||
    lowerTag.includes('commute') ||
    lowerTag.includes('connected')
  ) {
    return TagType.Transit;
  }
  if (lowerTag.includes('school') || lowerTag.includes('education')) {
    return TagType.Schools;
  }
  if (
    lowerTag.includes('green') ||
    lowerTag.includes('oasis') ||
    lowerTag.includes('park') ||
    lowerTag.includes('nature') ||
    lowerTag.includes('garden')
  ) {
    return TagType.Green;
  }
  if (lowerTag.includes('quiet') || lowerTag.includes('peaceful')) {
    return TagType.Quiet;
  }
  return TagType.Lively;
}

/**
 * Generate approximate scores from AI insights
 */
function generateMockScoresFromAI(insights: NeighbourhoodInsights): LocationScore[] {
  const scores: LocationScore[] = [];

  // 1. Connectivity - from transport hub quality
  if (insights.transportHub) {
    const qualityMap: Record<string, number> = {
      excellent: 90,
      good: 75,
      moderate: 60,
      poor: 50,
    };
    scores.push({
      id: 'transit',
      name: 'Connectivity',
      value: qualityMap[insights.transportHub.quality] || 70,
      description: insights.transportHub.description,
      type: ScoreType.Transit,
    });
  }

  if (
    insights.highlights.some(
      (h) => h.toLowerCase().includes('park') || h.toLowerCase().includes('playground'),
    )
  ) {
    scores.push({
      id: 'playground',
      name: 'Playground / Green Area',
      value: 75,
      description: 'Multiple parks and playgrounds within walking distance',
      type: ScoreType.Playground,
    });
  }

  if (
    insights.highlights.some(
      (h) => h.toLowerCase().includes('school') || h.toLowerCase().includes('education'),
    )
  ) {
    scores.push({
      id: 'education',
      name: 'Education',
      value: 80,
      description: 'Top-rated schools and kindergartens nearby',
      type: ScoreType.Education,
    });
  }

  return scores;
}

/**
 * Generate complete mock data for demonstration when all APIs fail
 */
function generateCompleteMockData(
  municipalityName: string,
  googlePlacesData?: {
    restaurants: any[];
    schools: any[];
    parks: any[];
    transit: any[];
    shopping: any[];
  },
): AggregatedNeighbourhoodData {
  return {
    name: municipalityName,
    description: `${municipalityName} is a vibrant neighbourhood that perfectly balances urban convenience with residential tranquility. Known for its excellent connectivity and family-friendly atmosphere, this area offers a high quality of life with easy access to essential amenities, green spaces, and cultural attractions.`,
    tags: [
      { id: 'tag-1', label: 'Family-Friendly', type: TagType.Family },
      { id: 'tag-2', label: 'Excellent Transit', type: TagType.Transit },
      { id: 'tag-3', label: 'Great Schools', type: TagType.Schools },
    ],
    scores: [
      {
        id: 'transit',
        name: 'Connectivity',
        value: 78,
        description: 'Good connections to public transport',
        type: ScoreType.Transit,
      },
      {
        id: 'playground',
        name: 'Playground / Green Area',
        value: 72,
        description: 'Multiple parks and playgrounds within walking distance',
        type: ScoreType.Playground,
      },
      {
        id: 'education',
        name: 'Education',
        value: 81,
        description: 'Top-rated schools and kindergartens nearby',
        type: ScoreType.Education,
      },
    ],
    aiInsights: {
      description: `${municipalityName} offers an ideal blend of urban amenities and residential comfort. The neighbourhood is characterized by its excellent public transportation links, making commuting effortless, while maintaining a peaceful atmosphere perfect for families.`,
      atmosphere: `${municipalityName} perfectly balances urban convenience with residential tranquility, offering excellent connectivity and a family-friendly atmosphere.`,
      lifestyleTags: ['Family-Friendly', 'Well-Connected', 'Green Spaces', 'Cultural Hub'],
      transportHub: {
        quality: 'excellent',
        description: 'Multiple tram and bus lines within 5 minutes',
      },
      greenSpace: {
        quality: 'good',
        description: 'Parks and nature areas within walking distance',
      },
      highlights: [
        'Close to top-rated schools and kindergartens',
        'Vibrant local markets and shopping districts',
        'Numerous parks and recreational facilities',
        'Strong sense of community with regular local events',
        'Safe neighbourhood with low crime rates',
      ],
      idealFor: [
        {
          persona: DEFAULT_PERSONA,
          score: 85,
          reasoning:
            'The neighbourhood excels in family amenities, with excellent schools like Schulhaus Hofacker nearby and safe parks for children to play.',
        },
      ],
    },
    macroData: {
      priceStatistics: {
        currentPricePerSqm: 9500,
        priceChange5Year: 21.8,
        marketTemperature: 'warm',
      },
      taxData: {
        municipalityName,
        canton: 'ZH',
        totalTaxRate: 3.7,
        taxFriendly: false,
        comparisonText: '15.6% higher than cantonal average',
      },
      marketData: {
        municipalityName,
        rentalYield: 3.5,
        occupancyRate: 97,
        investmentScore: 7.8,
        marketInsights: [
          'Strong rental market with high occupancy rates',
          'Properties typically sell within 6 weeks',
          'Consistent price appreciation over the past 5 years',
          'High demand from both buyers and renters',
        ],
      },
    },
    googlePlacesData,
    dataSource: 'mock',
    lastUpdated: new Date().toISOString(),
  };
}

/**
 * Extract region/canton code from municipality name
 * This is a simple heuristic - in production, use a proper mapping service
 */
function extractRegionFromMunicipality(municipalityName?: string): string | undefined {
  if (!municipalityName) return undefined;

  const municipality = municipalityName.toLowerCase();

  if (municipality.includes('zürich') || municipality.includes('zurich')) return 'ZH';
  if (
    municipality.includes('geneva') ||
    municipality.includes('genève') ||
    municipality.includes('geneve')
  )
    return 'GE';
  if (municipality.includes('basel')) return 'BS';
  if (municipality.includes('bern') || municipality.includes('berne')) return 'BE';
  if (municipality.includes('lausanne')) return 'VD';
  if (municipality.includes('lucerne') || municipality.includes('luzern')) return 'LU';
  if (municipality.includes('lugano')) return 'TI';
  if (municipality.includes('winterthur')) return 'ZH';
  if (municipality.includes('st. gallen') || municipality.includes('st gallen')) return 'SG';
  if (municipality.includes('zug')) return 'ZG';

  return undefined;
}
